import { CommonModule } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    HostBinding,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewEncapsulation,
    inject
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FeatureCollection } from 'geojson';
import { BehaviorSubject, Subject, debounceTime, take, takeUntil } from 'rxjs';

import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';

import { MapFilterComponent } from 'src/app/components/map-filter/map-filter.component';
import { MapComponent } from 'src/app/components/map/map.component';

import { AutoFocusDirective } from 'src/app/shared/directives/autofocus.directive';
import { QueryUnlocodes } from 'src/app/types/query-unlocode';

import { UnlocodeService } from 'src/app/services/unlocode.service';
import { GeoLocationService } from './services/geo-location.service';

/**
 * customer map
 * shows unlocodes and scope agents (customers) on an interactive map
 * @class CustomerMapComponent
 * @typedef {CommunityMapComponent}
 * @implements {OnInit}
 * @implements {AfterViewInit}
 * @implements {OnDestroy}
 */
@Component( {
    selector: 'app-community-map',
    imports: [
        AutoFocusDirective,
        CommonModule,
        MatFormFieldModule,
        MatAutocompleteModule,
        MatButtonModule,
        MatIconModule,
        ReactiveFormsModule,
        MatInputModule,
        MatChipsModule,
        MapFilterComponent,
        MapComponent
    ],
    templateUrl: './community-map.component.html',
    styleUrls: ['./community-map.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None
} )
export class CommunityMapComponent implements OnInit, AfterViewInit, OnDestroy {
    @HostBinding( 'class' ) class = 'community-map';
    @ViewChild( 'map' ) map!: MapComponent;

    // center of map defaults to Riege HQ
    center = [6.608872775079373, 51.28643943986161] as [number, number];

    // references on data collection
    agents: any = [];
    geoLocations!: FeatureCollection;
    locations? = new BehaviorSubject<[]>( [] );
    search = new FormControl( '' );

    /**
     * structure of custom buttons
     * which generate filter buttons on map
     */
    buttons = [
        { id: 'Airport', icon: 'flight', text: $localize`:@@button-label-air:Airport` },
        { id: 'Port', icon: 'directions_boat', text: $localize`:@@button-label-sea:Harbour` },
        { id: 'RailTerminal', icon: 'directions_railway', text: $localize`:@@button-label-rail:Rail terminal` }
    ];

    /**  lifecycle hook */
    readonly #destroyed: Subject<void> = new Subject<void>();
    readonly #loadData: Subject<void> = new Subject<void>();

    #geoLocationService = inject( GeoLocationService );
    #unlocodeService = inject( UnlocodeService );
    #activatedRoute = inject( ActivatedRoute );

    /** filter object which include {@linkQueryUnlocodes} parameters */
    #mapFilter: QueryUnlocodes = {};

    ngOnInit() {
        this.#activatedRoute.data.pipe( take( 1 ) ).subscribe( ( data ) => {
            this.agents = data['agents'];
        } );

        /**
         * subscribe on search input changes
         * and delay execution for 1 sec
         */
        this.search.valueChanges
            .pipe( takeUntil( this.#destroyed ), debounceTime( 250 ) )
            .subscribe( ( value: string | null ) => {
                if ( value && value !== '' ) {
                    // lookup results
                    this.searchLocation( value );
                } else {
                    // otherwise reset options
                    this.locations?.next( [] );
                }
            } );

        /**
         * subscribe to loadData subject
         * and delay execution for 1 sec
         */
        this.#loadData.pipe( takeUntil( this.#destroyed ), debounceTime( 1000 ) ).subscribe( () => {
            // clear data source
            this.#geoLocationService.clearDataSource();
            // load unlocode by geo-range (map rectangle)
            this.loadByGeoRange();
        } );
    }

    /**
     * when view is ready and map is loaded
     * then lookup unlocodes data in geo-range and set agent markers
     */
    ngAfterViewInit() {
        this.map.loaded.pipe( take( 1 ) ).subscribe( ( loaded: boolean ) => {
            if ( loaded ) {
                // add agent markers
                this.map.addMarkers( this.agents.features );
                this.#loadData.next();
            }
        } );
    }

    /**
     * gets bounds of map rectangle
     * then loads data within that geo-range
     */
    loadByGeoRange() {
        const bounds = this.map.getBounds();
        const query: QueryUnlocodes = {
            upperLatitude: bounds._ne.lat.toString(),
            lowerLatitude: bounds._sw.lat.toString(),
            leftLongitude: bounds._sw.lng.toString(),
            rightLongitude: bounds._ne.lng.toString(),
            ...this.#mapFilter
        };
        this.getUnlocodes( query );
    }

    /**
     * get list of unlocodes, filtered by query
     *
     * initially sourceId is set and cluster layers will be created
     * afterwards only the data gets updated and layers reloaded
     * @param query {object} object which include parameters
     *  which are part of {@link QueryUnlocodes}
     */
    getUnlocodes( query: QueryUnlocodes ) {
        this.#unlocodeService
            .getFilteredUnlocodes( query )
            .pipe( takeUntil( this.#destroyed ) )
            .subscribe( ( result ) => {
                const geodata: any = this.#geoLocationService.getGeoLocations( result.data );
                if ( !this.map.sourceId ) {
                    this.map.addClusterLayer( 'unlocodes', geodata );
                } else {
                    this.map.updateClusterLayer( 'unlocodes', geodata );
                }
            } );
    }

    /**
     * search location by name using maptiler geocoding api
     * @param value {string}
     */
    searchLocation( value: string ): void {
        this.#geoLocationService
            .searchByName( value )
            .pipe( take( 1 ) )
            .subscribe( ( res: any ) => {
                this.locations?.next( res.features );
            } );
    }

    /**
     * fly to selected location on map
     * @param location location given by geocoding forward search
     */
    showLocation( location: any ) {
        this.map.showLocation( location );
    }

    /**
     * set filter on map elements
     * @param filter {object} junctionTypes filter
     */
    setMapFilter( filter: QueryUnlocodes ) {
        this.#mapFilter = filter;
        this.#loadData.next();
    }

    /**
     * helper function to reload data
     * when the map has been moved
     */
    mapMoved() {
        this.#loadData.next();
    }

    ngOnDestroy() {
        this.#destroyed.next();
        this.#destroyed.complete();
    }
}
